import { Flex, Form, Image, Typography } from "antd";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import Button from "@/common/elements/Button";
import defaultAvatar from "@/assets/default_avatar.svg";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import dayjs from "dayjs";
import { isNonEmptyVariable } from "@/util/common_function";

const { Text } = Typography;

const PersonInformation = ({
  data,
  photos = [],
  selectedPhoto,
  photoIndex = 0,
  onPrevPhoto,
  onNextPhoto,
}) => {
  const [form] = Form.useForm();

  // Sync form values when selected record changes
  Form.useWatch([], form);
  const activePhoto = selectedPhoto ?? photos?.[0];
  const photoSrc = isNonEmptyVariable(activePhoto?.photo)
    ? `data:image/png;base64,${activePhoto.photo}`
    : defaultAvatar;

  const initialValues = {
    lastName: data?.lastName ?? "",
    firstName: data?.firstName ?? "",
    middleName: data?.middleName ?? "",
    bookingNumber: data?.bookingNumber ?? "",
    address: data?.address ?? "",
    city: data?.city ?? "",
    state: data?.state ?? "",
    zip: data?.zip ?? "",
    address2: data?.address2 ?? "",
    countryOfBirth: data?.countryOfBirth ?? "",
    phoneNumber: data?.phoneNumber ?? "",
    dob: data?.dob ? dayjs(data.dob) : null,
    ssn: data?.ssn ?? "",
    driverLicenseNumber: data?.driverLicenseNumber ?? "",
    driverLicenseState: data?.driverLicenseState ?? "",
    race: data?.race ?? "",
    gender: data?.gender ?? "",
    height: data?.height ?? "",
    weight: data?.weight ?? "",
    hairColor: data?.hairColor ?? "",
    eyeColor: data?.eyeColor ?? "",
    ethnicity: data?.ethnicity ?? "",
    skinTone: data?.skinTone ?? "",
    fbiNumber: data?.fbiNumber ?? "",
    stateId: data?.stateId ?? "",
    comments: data?.comments ?? "",
  };

  return (
    <StyledGridRow gutter={20} style={{ margin: "20px 0px" }}>
      <StyledGridCol lg={18}>
        <Form
          form={form}
          labelAlign="left"
          colon={true}
          labelCol={{ lg: 12 }}
          initialValues={initialValues}
          key={data?.transactionId} // re-initialize when record changes
        >
          <StyledGridRow gutter={16} align="middle">
            <StyledGridCol lg={6}>
              <FormItem label="Last Name" layout="vertical">
                <TextField value={data?.lastName ?? ""} disabled />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="First Name" layout="vertical">
                <TextField value={data?.firstName ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Middle Name" layout="vertical">
                <TextField value={data?.middleName ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Suffix" layout="vertical">
                <TextField value="" readOnly />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <FormItem label="Booking #" layout="vertical">
                <TextField value={data?.bookingNumber ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Address" layout="vertical">
                <TextField value={data?.address ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="City" layout="vertical">
                <TextField value={data?.city ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="State" layout="vertical">
                <TextField value={data?.state ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Zip" layout="vertical">
                <TextField value={data?.zip ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Address 2" layout="vertical">
                <TextField value={data?.address2 ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Country of Birth" layout="vertical">
                <TextField value={data?.countryOfBirth ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Phone" layout="vertical">
                <TextField value={data?.phoneNumber ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="DOB" layout="vertical">
                <CommonDatePicker
                  value={data?.dob ? dayjs(data.dob) : null}
                  disabled
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="SSN" layout="vertical">
                <TextField value={data?.ssn ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="DL Number" layout="vertical">
                <TextField value={data?.driverLicenseNumber ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="DL State" layout="vertical">
                <TextField value={data?.driverLicenseState ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <FormItem>
                <Checkbox
                  checked={data?.countryOfCitizenship === "US"}
                  disabled
                >
                  US Citizen
                </Checkbox>
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem>
                <Checkbox disabled>ICE 287/(g) Referral</Checkbox>
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <FormItem label="Race" layout="vertical">
                <TextField value={data?.race ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Gender" layout="vertical">
                <TextField value={data?.gender ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Height" layout="vertical">
                <TextField value={data?.height ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Weight" layout="vertical">
                <TextField value={data?.weight ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Hair" layout="vertical">
                <TextField value={data?.hairColor ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Eye" layout="vertical">
                <TextField value={data?.eyeColor ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Ethnicity" layout="vertical">
                <TextField value={data?.ethnicity ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Skin Tone" layout="vertical">
                <TextField value={data?.skinTone ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="FBI #" layout="vertical">
                <TextField value={data?.fbiNumber ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="State ID" layout="vertical">
                <TextField value={data?.stateId ?? ""} readOnly />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <FormItem>
                <Checkbox disabled>Detainee</Checkbox>
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem>
                <Checkbox
                  checked={isNonEmptyVariable(data?.commitedDate)}
                  disabled
                >
                  Committed
                </Checkbox>
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem>
                <Checkbox
                  checked={isNonEmptyVariable(data?.sorisOffenderId)}
                  disabled
                >
                  Sex Offender
                </Checkbox>
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Gang Affiliation" layout="vertical">
                <TextField value="" readOnly />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Housing Unit" layout="vertical">
                <TextField value="" readOnly />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={24}>
              <FormItem label="Comments" layout="vertical" labelCol={{ lg: 2 }}>
                <TextField
                  inputType="textarea"
                  value={data?.comments ?? ""}
                  readOnly
                />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </StyledGridCol>

      <StyledGridCol lg={4}>
        <Flex vertical align="center" gap="small">
          <div
            style={{
              width: 200,
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              border: "1px solid #d9d9d9",
              background: "#fafafa",
            }}
          >
            <Image
              src={photoSrc}
              width="100%"
              height="100%"
              style={{ objectFit: "contain" }}
            />
          </div>
          {photos.length > 0 && (
            <Flex align="center" gap="small">
              <Button
                type="text"
                color="default"
                variant="text"
                disabled={photoIndex <= 0}
                onClick={onPrevPhoto}
              >
                <CaretLeftOutlined />
              </Button>
              <Text>
                {photoIndex + 1}/{photos.length}
              </Text>
              <Button
                type="text"
                color="default"
                variant="text"
                disabled={photoIndex >= photos.length - 1}
                onClick={onNextPhoto}
              >
                <CaretRightOutlined />
              </Button>
            </Flex>
          )}
        </Flex>
      </StyledGridCol>
    </StyledGridRow>
  );
};

export default PersonInformation;
